/* חיוב כרטיס האשראי.
   ההוצאה הגדולה בחודש היא החיוב המרוכז של הכרטיס, ביום אחד בעו"ש.
   ‣ החיוב הקרוב: סך עסקאות האשראי בתקופה הנוכחית (או סכום ידני), ועוד חיובים קבועים בכרטיס
     ותשלומים ידועים שעוד לא נקלטו.
   ‣ חיובים בחודשים הבאים: חציון 3 התקופות הסגורות האחרונות. */
import { S, memo } from './state.js';
import { fISO, todayISO, iso10, dDays, median, absAmt } from '../lib/format.js';
import { getTxSrc, CARD_RX, isCardChargeRow } from './tx.js';
import { getKey, curKey, keyAdd, keyOfISO, periodStart, monthStart } from './periods.js';
import { recMatchAll, recSrcOf } from './recurring.js';
import { commitAlreadyScanned } from './commitments.js';

/** החיוב הקבוע שמייצג את החיוב המרוכז עצמו (לא חיוב שעובר בכרטיס) */
export function cardTemplate() {
  return S.rec.find((x) => x.type === 'expense' && (x.source || 'cash') !== 'credit' && CARD_RX.test(x.description || '')) || null;
}
export function cardChargeDay() {
  const r = cardTemplate();
  return r ? Number(r.day_of_month) || 10 : 10;
}

/** יום החיוב של מחזור k, כשתחילת החודש היא ms. דוח אשראי נשמר בתקופה שבה נופל יום החיוב שלו,
    ולכן כשמשנים את תחילת החודש, אותו מחזור יכול לעבור למפתח אחר */
export function cardChargeISOOfKey(k, ms, cd) {
  const y = parseInt(k.slice(0, 4), 10), m = parseInt(k.slice(5, 7), 10) - 1 + (ms > 1 && cd < ms ? 1 : 0);
  return fISO(new Date(y, m, Math.min(cd, new Date(y, m + 1, 0).getDate()), 12));
}

/* לאיזה חיוב כרטיס שייכת עסקה לפי התאריך שלה.
   התקופה באפליקציה היא 11 עד 10, אבל בישראכרט עסקה מה-10 כבר נכנסת לחיוב של החודש הבא.
   לומדים את זה מהדוחות עצמם. תשלומים לא נספרים, כי התאריך שלהם הוא יום החיוב ולא יום הקנייה. */
export function cardCutDay() {
  return memo('cardCutDay', () => {
    const ms = monthStart();
    if (ms <= 1) return null;
    const cnt = {};
    S.tx.forEach((t) => {
      if (getTxSrc(t) !== 'credit' || !t.month || !t.date) return;
      if (/ILS@/.test(t.orig_amount || '') || /\(תשלום/.test(t.description || '')) return;
      const d = new Date(iso10(t.date) + 'T12:00:00'), dd = d.getDate();
      if (dd < ms - 4 || dd >= ms) return;
      const k = getKey(d), c = (cnt[dd] = cnt[dd] || { same: 0, next: 0 });
      if (t.month === keyAdd(k, 1)) c.next++;
      else if (t.month === k) c.same++;
    });
    // רק יום שבו כמעט כל העסקאות עברו לחיוב הבא. סמוך לגבול יש עסקאות שנקלטות באיחור
    for (let d = ms - 4; d < ms; d++) {
      const c = cnt[d];
      if (c && c.next >= 2 && c.next >= 3 * c.same) return d;
    }
    return ms;
  });
}
/** יום החיוב הוא סוף התקופה (למשל תקופה 11 עד 10 וחיוב ב-10): כל מחזור חיוב הוא בדיוק תקופה אחת.
    אחרת (למשל חודש קלנדרי וחיוב ב-10) המחזור הוא מחיוב לחיוב, ומפתח המחזור הוא התקופה שבה נופל החיוב */
function cardAligned() {
  const ms = monthStart();
  return ms > 1 && cardChargeDay() < ms;
}
/** תאריך החיוב בחודש מסוים. חיוב ב-31 יורד בחודש קצר ביום האחרון שלו */
function chargeDateIn(y, m) {
  const dim = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(cardChargeDay(), dim), 12);
}
/** יום החיוב הראשון אחרי תאריך (לא כולל אותו) */
function nextChargeAfter(d) {
  const c = chargeDateIn(d.getFullYear(), d.getMonth());
  return fISO(c) > fISO(d) ? c : chargeDateIn(d.getFullYear(), d.getMonth() + 1);
}
/** מחזור החיוב הקרוב: העסקאות שיורדות בחיוב הבא */
export function cardCurKey() {
  if (cardAligned()) return curKey();
  return getKey(nextChargeAfter(new Date(todayISO() + 'T12:00:00')));
}
/** היום הראשון של מחזור k, להשוואת קצב בין מחזורים */
export function cardCycleStart(k) {
  if (cardAligned()) return periodStart(k);
  const prev = nextChargeAfter(new Date(periodStart(k).getTime() - 40 * 864e5));
  let c = prev;
  while (getKey(nextChargeAfter(c)) < k) c = nextChargeAfter(c);
  return c;
}
export function cardKeyOf(d) {
  if (!cardAligned()) return getKey(nextChargeAfter(d));
  const k = getKey(d), cut = cardCutDay(), ms = monthStart();
  if (cut != null && cut < ms && d.getDate() >= cut && d.getDate() < ms) return keyAdd(k, 1);
  return k;
}

/** סך פעולות האשראי לכל תקופה. upToDay: רק עד היום ה-N בתקופה, להשוואת קצב.
    גם עסקה שהוחרגה מהתקציב מחויבת בכרטיס. זיכוי מקטין את החיוב, כמו "סה"כ לחיוב" בדוח */
export function cardPeriodSums(upToDay) {
  return memo('cardPeriodSums|' + upToDay, () => {
    const out = {};
    S.tx.forEach((t) => {
      if (getTxSrc(t) !== 'credit' || !t.month) return;
      if (t.type !== 'expense' && t.type !== 'income') return;
      if (upToDay != null && dDays(iso10(t.date), cardCycleStart(t.month)) >= upToDay) return;
      const a = absAmt(t);
      out[t.month] = (out[t.month] || 0) + (t.type === 'income' ? -a : a);
    });
    return out;
  });
}
/** 3 התקופות הסגורות האחרונות שיש בהן פעילות אמיתית */
export function cardClosedKeys(sums) {
  const cur = cardCurKey();
  return Object.keys(sums).filter((k) => k < cur && sums[k] > 200).sort().slice(-3);
}
export function cardPeriodCredit() {
  const key = cardCurKey();
  return { month: key, amount: Math.round(cardPeriodSums()[key] || 0) };
}
export function cardFutureEstimate() {
  const sums = cardPeriodSums(), keys = cardClosedKeys(sums);
  return { amount: Math.round(median(keys.map((k) => sums[k]))), n: keys.length };
}

/** תשלומים (עסקה בתשלומים) בכל תקופה. מסומנים בסכום המקורי "ILS@" */
function cardInstSums() {
  return memo('cardInstSums', () => {
    const out = {};
    S.tx.forEach((t) => {
      if (getTxSrc(t) !== 'credit' || !t.month || t.type !== 'expense' || !/ILS@/.test(t.orig_amount || '')) return;
      out[t.month] = (out[t.month] || 0) + absAmt(t);
    });
    return out;
  });
}

/** הערכה לחיוב של מחזור עתידי k: החציון, ואם התשלומים הידועים לאותו מחזור גבוהים ממה שהיה בעבר,
    החציון בלי תשלומים ועוד התשלומים הידועים. בלי זה רכישה חדשה בתשלומים לא נכנסת לחודשים הבאים */
export function cardEstimateFor(k) {
  const sums = cardPeriodSums(), keys = cardClosedKeys(sums);
  if (!keys.length) return { amount: 0, n: 0, base: 0, plain: 0, due: 0, dueList: [] };
  const inst = cardInstSums();
  const base = median(keys.map((q) => sums[q]));
  const plain = median(keys.map((q) => sums[q] - (inst[q] || 0)));
  const dueList = cardCommitDue(k), due = dueList.reduce((s, x) => s + x.amount, 0);
  const withDue = plain + due > base;
  return { amount: Math.round(withDue ? plain + due : base), n: keys.length, base: Math.round(base), plain: Math.round(plain), due: Math.round(due), dueList, withDue };
}

/** קצב: כמה נרשם באשראי עד היום בתקופה, מול כמה בדרך כלל עד אותו יום */
export function cardPace() {
  const key = cardCurKey(), ps = cardCycleStart(key);
  const day = dDays(todayISO(), ps) + 1;
  const partial = cardPeriodSums(day), full = cardPeriodSums();
  const keys = cardClosedKeys(full);
  if (keys.length < 2) return null;
  const soFar = partial[key] || 0;
  const usual = median(keys.map((k) => partial[k] || 0));
  const usualFull = median(keys.map((k) => full[k]));
  const len = Math.round((new Date(ps.getFullYear(), ps.getMonth() + 1, ps.getDate(), 12) - ps) / 86400000);
  return {
    day, len, soFar: Math.round(soFar), usual: Math.round(usual), usualFull: Math.round(usualFull),
    proj: Math.round(soFar + Math.max(0, usualFull - usual)), pct: usual > 0 ? Math.round((soFar / usual - 1) * 100) : 0,
  };
}

/** תשלומים ידועים שמועדם בחיוב של מחזור k. לא מופיעים בעסקאות עד שהדוח של אותו חיוב נסרק */
export function cardCommitDue(k) {
  return S.commits.filter((c) => {
    if (!c.due_date || keyOfISO(c.due_date) !== k) return false;
    return !(c.kind === 'installment' && commitAlreadyScanned(c.description, c.amount, c.due_date));
  }).map((c) => ({ name: c.description, amount: absAmt(c), date: iso10(c.due_date), commit: true }));
}

/** סכום ידני שייך לחיוב אחד בלבד (המחזור שבו הוזן) */
export function cardManualFor(k) {
  const m = S.set.cardManual || {};
  return m.amount > 0 && (!m.key || m.key === k) ? m.amount : 0;
}

/** חיובים קבועים בכרטיס שעוד לא נמצאו בסך האשראי של התקופה (גם כאלה שעברו ועוד לא נסרקו) */
export function cardPendingRec() {
  const k = cardCurKey(), out = [];
  recMatchAll().occ.forEach((o) => {
    if (recSrcOf(o.r) !== 'credit' || o.r.type !== 'expense') return;
    if (cardKeyOf(o.d) !== k) return;
    if (o.st !== 'due' && o.st !== 'late' && o.st !== 'missing') return;
    out.push({
      name: o.r.description + (o.st === 'late' ? ' (טרם נקלט)' : o.st === 'missing' ? ' (לא נמצא בסריקה)' : ''),
      amount: o.amount, date: o.iso, st: o.st, recId: o.r.id,
    });
  });
  return out.sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** החיוב הקרוב: ידני, או סך האשראי בתקופה ועוד מה שעוד יגיע עד סוף התקופה */
export function cardChargeAmount() {
  return memo('cardChargeAmount', () => {
    const mk = cardManualFor(cardCurKey());
    if (mk > 0) return { amount: Math.round(mk), src: 'manual', parts: [] };
    const p = cardPeriodCredit();
    const cm = cardCommitDue(p.month), pend = cardPendingRec().concat(cm);
    const add = pend.reduce((s, x) => s + x.amount, 0), cms = cm.reduce((s, x) => s + x.amount, 0);
    if (p.amount > 0 || add > 0) return { amount: Math.round(p.amount + add), src: 'statement', month: p.month, base: Math.round(p.amount), parts: pend, commitSum: cms };
    return null;
  });
}

/** החיוב של החודש כבר נרשם כתנועה בעו"ש אחרי נקודת הפתיחה, ולכן כבר נספר */
export function cardPaidInBank(chargeISO, startStr) {
  const c = new Date(chargeISO + 'T12:00:00');
  const lo = fISO(new Date(c.getTime() - 3 * 864e5)), hi = fISO(new Date(c.getTime() + 5 * 864e5));
  return S.tx.some((t) => {
    const ds = iso10(t.date);
    // חיוב שנפל בסוף שבוע נרשם יום-יומיים אחרי. רק תנועה עתידית: מה שעבר כבר ביתרה
    if (!ds || ds <= startStr || ds < lo || ds > hi || t.type !== 'expense') return false;
    if (getTxSrc(t) === 'credit') return false;
    return CARD_RX.test(t.description || '');
  });
}

/** חיובי הכרטיס בטווח התחזית, לפי תאריך */
export function cardChargeEvents(start, days) {
  const c = cardChargeAmount(), fut = cardFutureEstimate();
  if ((!c || !c.amount) && !fut.amount) return {};
  const cur = cardCurKey(), startStr = fISO(start), out = {};
  for (let i = 0; i <= Math.ceil(days / 28) + 1; i++) {
    const d = chargeDateIn(start.getFullYear(), start.getMonth() + i);
    if (fISO(d) <= startStr) continue;
    if (dDays(d, start) > days) break;
    const iso = fISO(d);
    if (cardPaidInBank(iso, startStr)) continue;
    const k = getKey(d), isCur = k <= cur;
    let amt, src;
    // המחזור הנוכחי: תמיד מה שכבר נרשם בו, גם 0. הערכה לפי חודשים קודמים כבר כוללת הוצאות שעוד לא קרו,
    // ואז "אפשר להוציא" היה סופר אותן פעמיים
    if (isCur && c && c.amount) { amt = c.amount; src = c.src; }
    else if (isCur) { amt = 0; src = 'statement'; }
    else if (fut.amount) { amt = cardEstimateFor(k).amount; src = 'estimate'; }
    else if (c && c.amount) {
      // אין היסטוריה: כמו הקרוב, אבל תשלומים ידועים רק בחודש שבו הם יורדים
      amt = Math.round(c.amount - (c.commitSum || 0) + cardCommitDue(k).reduce((s, x) => s + x.amount, 0));
      src = 'copy';
      if (amt <= 0) continue;
    } else continue;
    const est = src === 'estimate' || src === 'copy';
    out[iso] = {
      name: 'חיוב כרטיס אשראי' + (src === 'manual' ? ' (ידני)' : est ? ' (הערכה)' : isCur ? ' (מה שנרשם עד עכשיו)' : ''),
      amount: amt, type: 'expense', estimated: est, cardSrc: src,
      base: !est && c && c.base != null ? c.base : amt, parts: !est && c ? c.parts || [] : [], card: true,
    };
  }
  return out;
}

/** חיוב כרטיס שהתאריך שלו עבר, אבל הוא עוד לא מופיע בעו"ש ולא בתוך היתרה שהוזנה.
    בלי זה, יום אחרי החיוב התזרים "שוכח" אותו עד שסורקים את העו"ש */
export function cardLateCharge() {
  return memo('cardLateCharge', () => {
    const today = todayISO(), t = new Date(today + 'T12:00:00');
    let d = chargeDateIn(t.getFullYear(), t.getMonth());
    if (fISO(d) > today) d = chargeDateIn(t.getFullYear(), t.getMonth() - 1);
    const iso = fISO(d);
    if (iso > today || dDays(t, d) > 25) return null;
    const { balDate, balEod } = S.set;
    if (balDate && (balEod ? iso <= balDate : iso < balDate)) return null; // כבר בתוך היתרה שהוזנה
    const lo = fISO(new Date(d.getTime() - 5 * 864e5));
    const paid = S.tx.some((x) => {
      const ds = iso10(x.date);
      if (!ds || x.type !== 'expense' || getTxSrc(x) === 'credit') return false;
      return ds >= lo && ds <= today && CARD_RX.test(x.description || '');
    });
    if (paid) return null;
    const k = getKey(d), sums = cardPeriodSums();
    let est = false;
    const m = S.set.cardManual || {};
    let amt = cardManualFor(k) && m.key === k ? Math.round(m.amount)
      : Math.round((sums[k] || 0) + cardCommitDue(k).reduce((s, x) => s + x.amount, 0));
    // אין עסקאות סרוקות לתקופה הזו: עדיף הערכה מאשר להתעלם מחיוב שבטוח ירד
    if (!amt) { amt = Math.round(cardFutureEstimate().amount || 0); est = amt > 0; }
    return amt > 0 ? { iso, amount: amt, key: k, est } : null;
  });
}

/** כשיום החיוב נופל בשבת, הבנק רושם אותו אחרי, לפעמים כבר בתקופה הבאה.
    לצורך סגירת תקופה החיוב נספר ביום החיוב, כי הוא שייך למחזור שהוא סוגר */
export function cardEffISO(t, ds) {
  if (!isCardChargeRow(t)) return ds;
  const d = new Date(ds + 'T12:00:00'), cd = cardChargeDay();
  const dim = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const c = new Date(d.getFullYear(), d.getMonth(), Math.min(cd, dim), 12);
  const gap = dDays(d, c);
  if (gap > 0 && gap <= 4 && getKey(c) !== getKey(d)) return fISO(c);
  return ds;
}
